/* eslint-disable @typescript-eslint/explicit-module-boundary-types, no-underscore-dangle */
import { SelectionModel } from '@angular/cdk/collections';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { AsyncPipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	NgZone,
	OnChanges,
	OnInit,
	Output,
	SimpleChanges,
	TemplateRef,
	ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSort, MatSortHeader, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, first, map, pairwise } from 'rxjs/operators';
import { BulkAction, EditActions, RowAction, TableAction, TableCellTemplate, TableColumn, TableConfig, TableFilter } from '@app/interfaces/table';
import { BaseServerFilterEntity } from '@app/models';
import { NgForKeyDirective, NgForTrackByItemDirective } from '../../directives/ng-for-track-by-id.directive';
import { KebabComponent } from '../kebab/kebab.component';

@Component({
	selector: 'sl-table',
	templateUrl: './table.component.html',
	styleUrls: ['./table.component.less'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		NgIf,
		MatToolbarModule,
		NgClass,
		NgFor,
		NgForKeyDirective,
		MatButtonModule,
		MatTableModule,
		MatSortModule,
		CdkDropList,
		MatCheckboxModule,
		MatTooltipModule,
		MatIconModule,
		CdkDragHandle,
		NgTemplateOutlet,
		KebabComponent,
		CdkDrag,
		MatPaginatorModule,
		MatSlideToggleModule,
		FormsModule,
		ReactiveFormsModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatSelectModule,
		NgForTrackByItemDirective,
		MatOptionModule,
		AsyncPipe,
	],
})
export class TableComponent implements OnInit, OnChanges, AfterViewInit {
	@ViewChild(MatSort, { static: true }) matSort: MatSort;
	@ViewChild(MatPaginator, { static: true }) matPaginator: MatPaginator;

	// templates
	@ViewChild('text') public text: TemplateRef<any>;
	@ViewChild('checkbox') public checkbox: TemplateRef<any>;
	@ViewChild('switch') public switch: TemplateRef<any>;
	@ViewChild('checkboxIcon') public checkboxIcon: TemplateRef<any>;
	@ViewChild('datepicker') public datepicker: TemplateRef<any>;
	@ViewChild('input') public input: TemplateRef<any>;
	@ViewChild('select') public select: TemplateRef<any>;

	@Input()
	config: TableConfig = {
		columns: [],
		actions: [],
		bulkActions: [],
		onRowClick: () => {
			// default, do nothing on row click
		},
		showRowSelect: true,
		showRowActions: true,
		defaultPageSize: 10,
		defaultSortField: 'Id',
		defaultSortDirection: 'asc',
		searchIncludes: [],
		searchExcludes: [],
		editableRows: false,
		editActions: null,
		selectedId: null,
		validateDropPosition: () => true,
	};

	@Input()
	data: any[] = [];

	@Input()
	filters: TableFilter<any> = { search: '' };

	@Input()
	newRow: any;

	@Input()
	noDataText = 'No data';

	@Input()
	pageSizeOptions: number[] = [5, 10, 25, 100];

	/**
	 * selectionSelectAll passed in as true will toggle all selections as [checked] = true.
	 */
	@Input()
	selectionSelectAll = false;

	/**
	 *  If true, default behavior of clearing selections on filter change is disabled.
	 */
	@Input()
	retainSelectionsOnFilter = false;

	/**
	 * Pass a list of data items that should be initially marked as selected. These items must come from the same
	 * source as the 'data' input.
	 */
	@Input()
	initialSelectedData: any[] = [];

	@Output()
	selectionChanged: EventEmitter<any[]> = new EventEmitter<any[]>();

	@Output()
	unsavedChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

	@Output()
	nestedUnsavedChanges: EventEmitter<boolean> = new EventEmitter<boolean>();

	@Output()
	pageChanged: EventEmitter<{ pageIndex: number; pageSize: number }> = new EventEmitter<{ pageIndex: number; pageSize: number }>();

	@Output()
	sortChanged: EventEmitter<Sort> = new EventEmitter<Sort>();

	@Output()
	rowExpanded: EventEmitter<any> = new EventEmitter<any>();

	@Output()
	fieldChanged: EventEmitter<any> = new EventEmitter<any>();

	columns: TableColumn[] = [];
	actions: RowAction[] = [];
	tableActions: TableAction[] = [];
	bulkActions: BulkAction[] = [];
	selection = new SelectionModel<any>(true, []);
	focusedRow = new SelectionModel<any>(false, null);
	editableRows = new SelectionModel<any>(true, []);
	initialRows = new Map();
	defaultSortField = 'Description';
	defaultSortDirection = 'asc';
	tableColumns = [];
	displayedColumns = [];
	displayedColumnsSecondTable = [];
	dataSource: MatTableDataSource<any>;
	editActions?: EditActions;
	editFormGroups = new Map();
	defaultPageSize = 10;
	stickyHeader: boolean;
	excludePaginator?: boolean;
	alwaysShowBulkActionToolbar?: boolean;
	hideBulkActionToolbar?: boolean;
	hideBulkActionButtons?: boolean;
	data$: Subject<any[]> = new BehaviorSubject<any[]>(this.data);
	hasExpandableRows$: Observable<boolean> = this.data$.pipe(map((data) => data.some((row) => this.isExpandable(row))));
	dropNameColumn: string = null;

	get pageIndex(): number {
		return this.matPaginator?.pageIndex ?? 0;
	}

	get pageSize(): number {
		return this.matPaginator?.pageSize ?? this.defaultPageSize;
	}

	noFilteredDataText = 'No data matching the filter';

	dataIsFiltered: boolean;

	columnFilterMap = {};
	isAllSelected = false;
	isPageSelectAllChecked = false;
	isPageSelectAllIndeterminate = false;

	private page: number;
	private sort: Sort;

	constructor(
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly zone: NgZone,
	) {
		this.dataSource = new MatTableDataSource();
		this.selection.changed.subscribe((changed) => {
			this.selectionChanged.emit(changed.source.selected);
			// Update selection states whenever selection changes
			setTimeout(() => {
				this.updateAllSelectedState();
				this.updatePageSelectAllCheckboxState();
				this.setRowSelectedField();
			}, 0);
		});
	}

	/*
	 * Method to manually trigger Angular change detection mechanism
	 */
	triggerChangeDetection() {
		this.changeDetectorRef.markForCheck();
	}

	ngAfterViewInit(): void {
		// setTimeout is avoiding the error "NG0100: ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked."
		// https://blog.angular-university.io/angular-debugging/
		setTimeout(() => {
			if (this.data.length > 0) {
				this.updateTableData();
				// this.updateTableData() calls onBulkActionCancel() which sets the selection to empty, so populate the initial selection after that.
				this.updateInitialSelection();
			}

			// Initialize selection states
			this.updateAllSelectedState();
			this.updatePageSelectAllCheckboxState();

			// Listen for sort changes and update checkbox state
			if (this.matSort) {
				this.matSort.sortChange.subscribe(() => {
					// Use setTimeout to ensure the sort has been applied before updating checkbox state
					setTimeout(() => this.updatePageSelectAllCheckboxState(), 0);
					this.sortChanged.emit({ active: this.matSort.active, direction: this.matSort.direction });
				});
			}

			// Listen for pagination changes and update checkbox state
			if (this.matPaginator) {
				this.matPaginator.page.subscribe(() => {
					// Use setTimeout to ensure the page change has been applied before updating checkbox state
					setTimeout(() => this.updatePageSelectAllCheckboxState(), 0);
					this.pageChanged.emit({ pageIndex: this.pageIndex, pageSize: this.pageSize });
				});
			}
		});
	}

	getTemplate({
		column,
		row,
		isFocused,
		alwaysEditable,
	}: {
		column: TableColumn;
		row: any;
		isFocused: boolean;
		alwaysEditable: boolean;
	}): TemplateRef<any> | TableCellTemplate {
		let template = column.template;

		if (this.config.editableRows && !column.templateOptions?.useCustomTemplateRef) {
			if ((this.config.isReadOnly || !this.isEditable(row)) && !alwaysEditable) {
				if (template === 'checkbox') {
					return this.checkboxIcon;
				}
				return this.text;
			}
		}

		if (typeof column.template == 'function') {
			template = column.template({
				row,
				isFocused,
				isEditable: column.isEditable,
			});
		}

		if (template instanceof TemplateRef) {
			return template;
		}

		switch (template) {
			case 'input':
				return this.input;
			case 'checkbox':
				return this.checkbox;
			case 'datepicker':
				return this.datepicker;
			case 'select':
				return this.select;
			case 'text':
			default:
				return this.text;
		}
	}

	getFormControl(row: any, column: TableColumn, emitOnChange: boolean): UntypedFormControl {
		if (!this.editActions) {
			return null;
		}

		const id = row[this.editActions.idProperty];
		if (!this.editFormGroups.has(id)) {
			const formGroupToAdd = new UntypedFormGroup({}, { validators: this.editActions.validators });
			const idControl = new UntypedFormControl(this.editActions.idProperty);
			idControl.setValue(id);
			formGroupToAdd.addControl(String(this.editActions.idProperty), idControl);
			this.editFormGroups.set(id, formGroupToAdd);
		}

		const formGroup = this.editFormGroups.get(id);
		let control = formGroup.get(String(column.key));

		if (!control) {
			formGroup.addControl(String(column.key), new UntypedFormControl(''));
			control = formGroup.get(String(column.key));

			if (emitOnChange) {
				control.valueChanges
					.pipe(
						distinctUntilChanged(),
						pairwise(), // gets a pair of old and new value
					)
					.subscribe(([oldValue, newValue]) => {
						if (oldValue !== newValue) {
							this.nestedUnsavedChanges.emit(true);
						}
					});
			}
		}

		return control;
	}

	getActions(row: any): RowAction[] {
		return this.actions.filter((x) => (x.hidden ? !x.hidden(row) : true));
	}

	onBulkActionClick(bulkAction: BulkAction) {
		bulkAction?.onClick?.(this.selection.selected);
	}

	isFunction(value: any) {
		return typeof value === 'function';
	}

	//#region Table Row Selection Logic

	get sortedFilteredData() {
		return this.dataSource._orderData(this.dataSource.filteredData);
	}

	getSelectableData() {
		return this.sortedFilteredData.filter(this.checkRowCanBeSelected);
	}

	getPageData(): any[] {
		if (this.config?.hideBulkActionToolbar) {
			return this.sortedFilteredData;
		}
		return this.dataSource._pageData(this.sortedFilteredData);
	}

	getSelectablePageData(): any[] {
		const pageData = this.getPageData();
		return pageData.filter(this.checkRowCanBeSelected);
	}

	/**
	 * Get the page index where a row with specific ID is located
	 * @param id The ID of the row to find
	 * @param idProperty The property name to use as ID (defaults to 'Id')
	 * @returns The page index (0-based) or -1 if not found
	 */
	getPageIndexForRowId(id: any, idProperty: string = 'Id'): number {
		const rowIndex = this.sortedFilteredData.findIndex((item) => item[idProperty] === id);

		if (rowIndex === -1) {
			return -1; // Row not found
		}

		const pageSize = this.pageSize;
		return Math.floor(rowIndex / pageSize);
	}

	/**
	 * Remove any selected rows that are no longer selectable due to changes in filters or other conditions.
	 */
	removeFilteredSelections() {
		const selectedRows = this.allSelectedRows;
		this.selection.clear();
		this.selection.select(...selectedRows);
	}

	/**
	 * Clear only the selected rows which are selectable, leaving disabled rows as-is.
	 */
	clearSelectableRows() {
		const selectableRows = this.getSelectableData();
		this.selection.deselect(...selectableRows);
	}

	get isEntirePageSelected(): boolean {
		const pageData = this.getSelectablePageData();
		return pageData.length > 0 && pageData.every(this.isRowSelected);
	}

	/*
	 * Get the list of all rows that are selected on any page
	 */
	get allSelectedRows(): any[] {
		const filteredRows = this.sortedFilteredData;
		return filteredRows.filter(this.isRowSelected);
	}

	get isNoneSelectedOnPage(): boolean {
		return this.numSelectedOnPage === 0;
	}

	get numSelectedOnPage(): number {
		const pageData = this.getPageData();
		return pageData.filter(this.isRowSelected).length;
	}

	get numSelectableOnPage(): number {
		const selectablePageData = this.getSelectablePageData();
		return selectablePageData.length;
	}

	get hasSelectionOnPage(): boolean {
		return this.numSelectedOnPage > 0;
	}

	get selectAllDisabled(): boolean {
		return this.numSelectableOnPage === 0;
	}

	get numSelectable(): number {
		return this.sortedFilteredData.filter(this.checkRowCanBeSelected).length;
	}

	get numSelected(): number {
		return this.selection.selected.length;
	}

	get numSelectableSelected(): number {
		return this.selection.selected.filter(this.checkRowCanBeSelected).length;
	}

	get hasMoreThanOnePage(): boolean {
		return this.matPaginator?.getNumberOfPages() > 1;
	}

	/**
	 * Determines whether the "Clear selected" button should be shown in the bulk action toolbar.
	 * When bulk action buttons are shown, retain the original behavior, where the button is shown only
	 * when all rows are selected, and there are selectable rows that have been selected. The bulk actions
	 * buttons always include a "Cancel" button which does the same thing.
	 * When bulk action buttons are hidden, the "Clear selected" button is shown any time there are selectable
	 * rows that have been selected.
	 */
	get showClearAll(): boolean {
		return (this.hideBulkActionButtons || this.isAllSelected) && this.numSelectableSelected > 0;
	}

	get showSelectedTotal() {
		return !this.isAllSelected && (this.alwaysShowBulkActionToolbar || this.numSelectedOnPage !== this.numSelected);
	}

	get showSelectAll(): boolean {
		const selectable = this.numSelectable;
		const selected = this.numSelectableSelected;
		// If the number of selectable rows changed due to a change in disabled condition of the rows, but the number of
		// selected rows hasn't changed, make sure the isAllSelected state is correct.
		if (!this.isAllSelected && selected === selectable) {
			this.isAllSelected = true;
		}
		return !this.isAllSelected && selectable > 0 && selectable > selected;
	}

	get showBulkActionToolbar(): boolean {
		return this.alwaysShowBulkActionToolbar || (this.numSelected > 0 && this.hideBulkActionToolbar !== true);
	}

	get hasInitialSelectedData(): boolean {
		return this.initialSelectedData && this.initialSelectedData.length > 0;
	}

	onPageSelectAllClicked(event) {
		// Get the checkbox's checked state from the event
		const isChecked = event.checked;
		const selectablePageData = this.getSelectablePageData();

		if (isChecked) {
			// If checkbox is now checked, select all rows on the page
			this.selection.select(...selectablePageData);
		} else {
			// If checkbox is now unchecked, deselect all rows on the page
			this.selection.deselect(...selectablePageData);
		}
	}

	/*
	 * Method to update the page select all checkbox state
	 */
	updatePageSelectAllCheckboxState() {
		const pageData = this.getPageData();
		const numTotalOnPage = pageData.length;
		const numTotalSelectedOnPage = pageData.filter(this.isRowSelected).length;
		const selectablePageData = this.getSelectablePageData();
		const numSelectableOnPage = selectablePageData.length;
		const numSelectableSelectedOnPage = selectablePageData.filter(this.isRowSelected).length;

		// Update the checkbox states
		this.isPageSelectAllChecked = numTotalSelectedOnPage === numTotalOnPage || numSelectableSelectedOnPage === numSelectableOnPage;
		// Indeterminate state only matters for selectable rows
		this.isPageSelectAllIndeterminate = numSelectableSelectedOnPage > 0 && numSelectableSelectedOnPage < numSelectableOnPage;

		// Force change detection to ensure the checkbox updates
		this.changeDetectorRef.markForCheck();
	}

	/**
	 * Update the all selected state based on the current selection.
	 */
	updateAllSelectedState() {
		this.isAllSelected = this.numSelectableSelected === this.numSelectable;
	}

	onSelectAllClicked() {
		if (this.isAllSelected) {
			return;
		}
		const selectableData = this.getSelectableData();
		this.selection.select(...selectableData);
		setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
	}

	onClearAllClicked() {
		this.clearSelectableRows();
		setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
	}

	isRowSelected = (row) => this.selection.isSelected(row) || !this.checkRowCanBeSelected(row);

	setRowSelectedField() {
		if (this.config?.selectionField) {
			this.data.forEach((row) => {
				row[this.config.selectionField] = this.selection.isSelected(row);
			});
		}
	}

	checkboxLabel(row?: any): string {
		if (!row) {
			return `${this.isEntirePageSelected ? 'select' : 'deselect'} all`;
		}
		return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
	}

	onBulkActionCancel() {
		this.selection.clear();
		this.isAllSelected = false;
	}

	//#endregion

	isDragDisabled() {
		return !(this.config.enableMove && this.editableRows.isEmpty());
	}

	searchFilterTransform = (row: any): string =>
		this.columns
			.filter((column: TableColumn) => {
				// excluded from search unless explicitly defined in searchIncludes
				const hidden = typeof column.hidden === 'function' ? column.hidden() : column.hidden;
				if (Array.isArray(this.config.searchIncludes) && this.config.searchIncludes.length > 0) {
					return this.config.searchIncludes.includes(String(column.key));
				}
				return !hidden;
			})
			.map((column: TableColumn) => {
				if (column.transform) {
					return column.transform({ row, value: row[column.key], key: column.key });
				}
				return row[column.key];
			})
			.join('');

	searchFilter = (data: any, filter: string): boolean => {
		const defaultData = this.searchFilterTransform(data) || '';
		return defaultData.toString().trim().toLowerCase().indexOf(filter.trim().toLowerCase()) !== -1;
	};

	renderHeader({ column, row }): string {
		if (typeof column.header === 'function') {
			return column.header({ row, column });
		}
		return column.header;
	}

	renderTextCell({ column, row }): string {
		if (typeof column.transform === 'function') {
			return column.transform({ row, value: row[column.key], key: column.key });
		}
		if (row[column.key] !== null && row[column.key] !== undefined) {
			let value = String(row[column.key]);
			if (column.shortenText) {
				value = this.shortenText(value);
			}
			return value;
		}
		return '';
	}

	onRowSelected(row): void {
		this.selection.toggle(row);
	}

	onRowClick(row): void {
		this.focusedRow.select(row);
		this.config?.onRowClick?.(row);
	}

	onTableActionsOpened(event: any, row: any): void {
		event?.stopPropagation();
		if (this.config.onTableActionsOpened) {
			this.config.onTableActionsOpened(row);
		}
	}

	filterPredicate = (row: any, filterJson: string) => {
		if (this.isEditable(row)) {
			//Don't filter out editable rows
			return true;
		}

		const filters: TableFilter<any> = JSON.parse(filterJson);
		const search = filters.search || '';

		let searchFilterMatch = true;
		// default angular material filter where it searches against all values
		if (search) {
			searchFilterMatch = this.searchFilter(row, search);
		}

		// Filter based on columns
		const filtersMatch = Object.keys(filters)
			.filter((key) => key !== 'search')
			.every((key) => {
				const filterValue = filters[key];
				let match = true;
				if (key !== null && filterValue !== null && filterValue !== undefined && filterValue !== '') {
					const value = row[key];
					const columnFilter = this.columnFilterMap[key];
					// custom filter function provided
					if (columnFilter && typeof columnFilter === 'function') {
						match = columnFilter({
							row,
							key,
							filterValue,
							value,
						});
					} else {
						match = String(value) === String(filterValue);
					}
				}
				return match;
			});

		return searchFilterMatch && filtersMatch;
	};

	sortingDataAccessor(data: any, sortHeaderId: string): string | number {
		if (typeof data[sortHeaderId] === 'string') {
			if (!isNaN(Number(data[sortHeaderId]))) {
				return parseFloat(data[sortHeaderId]);
			}

			return data[sortHeaderId].toLocaleLowerCase();
		}
		return data[sortHeaderId];
	}

	ngOnInit(): void {
		this.initializeTableConfig();
	}

	initializeTableConfig() {
		const {
			actions,
			bulkActions = [],
			tableActions = [],
			columns,
			expandedColumns,
			defaultSortDirection,
			defaultSortField,
			defaultPageSize = 10,
			showRowSelect = false,
			showRowActions = false,
			editableRows = false,
			enableMove = false,
			editActions,
			isReadOnly,
			stickyHeader = false,
			excludePaginator = false,
			alwaysShowBulkActionToolbar = false,
			hideBulkActionToolbar = false,
			hideBulkActionButtons = false,
		} = this.config;

		this.columns = columns;
		this.defaultPageSize = defaultPageSize;
		this.defaultSortDirection = defaultSortDirection;
		this.defaultSortField = String(defaultSortField);
		this.actions = actions;
		this.bulkActions = (bulkActions || []).filter((item) => !item.hidden || !item.hidden());
		this.editActions = editActions;
		this.tableActions = tableActions;
		this.stickyHeader = stickyHeader;
		this.excludePaginator = excludePaginator;
		this.alwaysShowBulkActionToolbar = alwaysShowBulkActionToolbar;
		this.hideBulkActionToolbar = hideBulkActionToolbar;
		this.hideBulkActionButtons = hideBulkActionButtons;

		if (editActions && editActions.idProperty == null) {
			editActions.idProperty = 'Id';
		}

		this.dataSource.sortingDataAccessor = this.sortingDataAccessor;
		this.dataSource.filterPredicate = this.filterPredicate;

		this.columns.forEach((column) => {
			this.columnFilterMap[column.key] = column.filter;
		});
		const displayedColumns = [...this.columns.filter((x) => (typeof x.hidden === 'function' ? !x.hidden() : !x.hidden)).map((x) => x.key)];

		if (showRowSelect) {
			displayedColumns.unshift('select');
		}

		if (expandedColumns) {
			displayedColumns.unshift('expand');
		}

		if (showRowActions) {
			displayedColumns.push('actions');
		}

		if (editableRows && !isReadOnly) {
			displayedColumns.push('editable');
		}

		if (enableMove && !isReadOnly) {
			if (this.config.dropNameColumn) {
				this.dropNameColumn = this.config.dropNameColumn;
			}
			displayedColumns.push('move');
		}

		if (!this.config.validateDropPosition) {
			this.config.validateDropPosition = () => true;
		}

		this.displayedColumns = displayedColumns;

		if (expandedColumns) {
			this.displayedColumnsSecondTable = expandedColumns.map((x) => x.key);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.config) {
			this.initializeTableConfig();
		}

		if (changes.data) {
			this.data = changes.data.currentValue;
			this.data$.next(changes.data.currentValue);
			this.updateTableData();
		}

		if (changes.filters) {
			const filters = changes.filters.currentValue;
			this.dataIsFiltered = Object.entries(filters).some(([, value]) => value != null && value !== '');
			this.updateTableFilters(filters);
		}

		if (changes.initialSelectedData) {
			this.initialSelectedData = changes.initialSelectedData.currentValue;
			this.updateInitialSelection();
		}

		if (changes.pageSize) {
			this.updatePageSize(changes.pageSize.currentValue);
		}

		if (changes.newRow) {
			this.addRow(changes.newRow.currentValue);
		}

		if (changes.selectionSelectAll?.currentValue && changes.selectionSelectAll.currentValue !== changes.selectionSelectAll.previousValue) {
			this.selection.select(...this.data);
		}
	}

	updateTableFilters(filters: TableFilter<any>): void {
		const dsFilters = filters
			? Object.fromEntries(Object.entries(filters).filter((kv) => !BaseServerFilterEntity.isBaseServerFilterEntity(kv[1])))
			: filters;
		this.dataSource.filter = JSON.stringify(dsFilters);

		if (!this.retainSelectionsOnFilter) {
			// Only remove selections that are no longer available in the filtered data
			// This is to ensure that selections are not lost when filters change
			this.removeFilteredSelections();
		} else {
			// Selections are retained even though the selected row is no longer visible,
			// so just update the checkbox state of the header checkbox
			this.updatePageSelectAllCheckboxState();
		}

		// Force change detection to ensure the bulk toolbar is updated
		setTimeout(() => this.changeDetectorRef.detectChanges(), 0);
	}

	updateTableData(): void {
		if (!this.dataSource.sort) {
			this.dataSource.sort = this.matSort;
		}
		if (!this.dataSource.paginator && this.defaultPageSize > 0) {
			this.dataSource.paginator = this.matPaginator;
		}
		this.onBulkActionCancel();
		this.dataSource.data = this.data;
		this.zone.onStable.pipe(first()).subscribe(() => {
			this.updatePageIndex(this.page);
			this.updateSort(this.sort);
		});
	}

	updateInitialSelection(): void {
		this.selection.clear();
		this.isAllSelected = false;
		if (this.hasInitialSelectedData) {
			this.selection.select(...this.initialSelectedData);
		}
	}

	updatePageSize(pageSize: number) {
		if (this.matPaginator) {
			this.matPaginator.pageSize = pageSize;
			// Update checkbox state after page size change as the page data has changed
			this.updatePageSelectAllCheckboxState();
		}
	}

	updatePageIndex(pageIndex = 0) {
		if (this.matPaginator) {
			this.page = pageIndex;
			this.matPaginator.pageIndex = pageIndex;
			// required to emit a page event to trigger rerender
			// https://github.com/angular/components/issues/8417#issuecomment-564462445
			this.matPaginator.page.emit({
				length: this.matPaginator.getNumberOfPages(),
				pageIndex,
				pageSize: this.matPaginator.pageSize,
			});

			// Update checkbox state after page change as the page data has changed
			this.updatePageSelectAllCheckboxState();
		}
	}

	updateSort(sort: Sort) {
		if (sort && this.matSort) {
			this.sort = sort;
			this.matSort.sortChange.emit(sort);
			this.matSort.active = sort.active;
			this.matSort.direction = sort.direction;
			// to show the arrow after programatically setting the sort
			// https://github.com/angular/components/issues/10242#issuecomment-470726829
			(this.matSort.sortables.get(sort.active) as MatSortHeader)?._setAnimationTransitionState({ toState: 'active' });

			// Update checkbox state after sort changes as the page data may have changed
			this.updatePageSelectAllCheckboxState();
		}
	}

	startEditing(row: any, e: Event): void {
		e?.stopPropagation();
		if (row) {
			this.initialRows.set(row[this.editActions.idProperty], { ...row });
			this.editableRows.select(row[this.editActions.idProperty]);
			const fg = this.editFormGroups.get(row[this.editActions.idProperty]);
			this.syncFormGroupControls(fg, row, false);
			this.emitUnsavedChanges();

			if (row.isExpanded) {
				row.isExpanded = false;
				this.changeDetectorRef.markForCheck();
			}
		}
	}

	checkRowCanBeSelected = (row: any): boolean => !this.config.selectionOptions?.disabled?.(row);

	disabledRowCheckBoxTooltip(row: any): string {
		if (this.config.selectionOptions) {
			return typeof this.config.selectionOptions.disabledMessage === 'function'
				? this.config.selectionOptions.disabledMessage(row)
				: this.config.selectionOptions.disabledMessage;
		}
		return null;
	}

	applyEditing(row: any, e: Event): void {
		e?.stopPropagation();
		if (this.editActions) {
			const fg = this.editFormGroups.get(row[this.editActions.idProperty]);

			if (fg.valid) {
				this.syncFormGroupControls(fg, row, true);
				this.editActions.update(row);
				this.editableRows.deselect(row[this.editActions.idProperty]);
				this.emitUnsavedChanges();
				this.fieldChanged.emit(this.editFormGroups);
			} else {
				fg.markAllAsTouched();
			}
		} else {
			this.editableRows.deselect(row[this.editActions.idProperty]);
			this.emitUnsavedChanges();
		}
	}

	syncFormGroupControls(fg: UntypedFormGroup, row: any, isRowUpdate: boolean): void {
		for (const key in fg?.controls) {
			if (Object.prototype.hasOwnProperty.call(fg.controls, key)) {
				const control = fg.controls[key] as UntypedFormControl;

				// Always try to set up validators when initializing controls
				if (!isRowUpdate) {
					const column = this.columns.find((c) => c.key === key);
					if (column && column.templateConfig?.validators?.length > 0) {
						control.setValidators(column.templateConfig.validators);
						control.updateValueAndValidity();
					}
				}

				// Handle value updates
				if (row[key] !== control.value) {
					if (isRowUpdate) {
						row[key] = control.value;
					} else {
						control.setValue(row[key]);
					}
				}
			}
		}
	}

	getControlErrorMessage(control: UntypedFormControl, column: TableColumn): string {
		const keys = Object.keys(control?.errors) || [];
		if (keys.length > 0) {
			const errorKey = keys[0];
			const error = column.templateConfig?.validatorMessages?.[errorKey];
			if (error) {
				return error;
			}
		}
		return 'Invalid';
	}

	isEditable(row: any): boolean {
		if (row && this.editActions) {
			return this.editableRows.isSelected(row[this.editActions.idProperty]);
		}
		return false;
	}

	deleteRow(row: any, e: Event): void {
		e?.stopPropagation();
		if (this.editActions) {
			this.editActions.delete(row);
			this.emitUnsavedChanges();
		}
	}

	allowDelete(row: any, e: Event): boolean {
		e?.stopPropagation();
		if (this.editActions?.allowDelete) {
			return this.editActions.allowDelete(row);
		}

		return true;
	}

	allowUpdate(row: any, e: Event): boolean {
		e?.stopPropagation();
		if (this.editActions?.allowUpdate) {
			return this.editActions.allowUpdate(row);
		}

		return true;
	}

	rejectEditing(row: any, e: Event): void {
		e?.stopPropagation();
		const id = row[this.editActions.idProperty];

		if (id < 0) {
			this.editActions.delete(row);
			this.editFormGroups.delete(id);
		} else {
			Object.assign(row, this.initialRows.get(id));
			this.initialRows.delete(id);
		}

		this.editableRows.deselect(id);
		this.emitUnsavedChanges();
	}

	addRow(row: any): void {
		this.startEditing(row, undefined);
		this.updateTableFilters(this.filters);
	}

	dropTable(event: CdkDragDrop<any[]>): void {
		const offset = event.previousIndex - event.currentIndex;
		this.config.onDrop(event.item.data, offset);
	}

	get collapseRowsEnabled(): boolean {
		const { expandedColumns, expandedUseArrowIcons } = this.config;
		return expandedColumns && expandedUseArrowIcons;
	}

	get hasExpanded(): boolean {
		const pageData = this.getPageData();
		return pageData.some((r) => r.isExpanded === true);
	}

	collapseAll(): void {
		if (this.collapseRowsEnabled && this.hasExpanded) {
			const pageData = this.getPageData();
			pageData.forEach((r) => (r.isExpanded = false));
			this.changeDetectorRef.markForCheck();
		}
	}

	expandCollapse(row): void {
		row.isExpanded = row.isExpanded === true ? false : true;
		if (row.isExpanded) {
			this.rowExpanded.emit(row);
		}
		this.changeDetectorRef.markForCheck();
	}

	isExpandable(row): boolean {
		const hideExpandToggle = this.config.selectionOptions?.hideExpandToggle?.(row) ?? false;
		let isExpandable = hideExpandToggle === false;
		if (isExpandable) {
			isExpandable = this.getExpandedDatasource(row).data !== null && !this.isEditable(row);
		}
		return isExpandable;
	}

	getExpandedDatasource(row): MatTableDataSource<any> {
		return new MatTableDataSource<any>(row[this.config.expandedColumnName]);
	}

	private emitUnsavedChanges(): void {
		this.updateTableFilters(this.filters);
		this.unsavedChanges.emit(this.editableRows.selected.length > 0);
	}

	private shortenText(value: string): string {
		let addElipsis = false;
		const maxLines = 3;
		//Limit to maxLines of text
		if (value.split('\n').length >= maxLines) {
			value = value.split('\n').slice(0, maxLines).join('\n');
			addElipsis = true;
		}

		const maxCharacters = 50;
		if (value.length > maxCharacters) {
			const limit = value.indexOf(' ') >= 0 ? value.substring(0, maxCharacters).lastIndexOf(' ') : maxCharacters;
			value = value.substring(0, limit);
			addElipsis = true;
		}
		if (addElipsis) {
			value += '...';
		}

		return value;
	}

	private isSticky(column: TableColumn) {
		if ('sticky' in column && column.sticky) {
			return true;
		}

		return false;
	}
}
